import * as React from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { cn } from "@/lib/utils"; // optional helper

const DrawerContext = React.createContext({
  side: "right",
  portalContainer: null,
  setPortalContainer: () => {},
});

export function Drawer({ side = "right", children, ...props }) {
  const [portalContainer, setPortalContainer] = React.useState(null);
  return (
    <DrawerContext.Provider value={{ side, portalContainer, setPortalContainer }}>
      <Dialog.Root {...props}>{children}</Dialog.Root>
    </DrawerContext.Provider>
  );
}

function useDrawer() {
  return React.useContext(DrawerContext);
}

/** When used inside {@link Drawer}, returns the dialog content element for portaling overlays (e.g. combobox). */
export function useDrawerPortalContainer() {
  const { portalContainer } = useDrawer();
  return portalContainer ?? undefined;
}

// Trigger
const DrawerTrigger = React.forwardRef(function DrawerTrigger(
  { className, ...props },
  ref
) {
  return <Dialog.Trigger ref={ref} className={className} {...props} />;
});

// Overlay
const DrawerOverlay = React.forwardRef(function DrawerOverlay(
  { className, ...props },
  ref
) {
  return (
    <Dialog.Overlay
      ref={ref}
      className={cn(
        "fixed inset-0 z-backdrop bg-black/40 backdrop-blur-sm",
        "data-[state=open]:animate-in data-[state=closed]:animate-out",
        "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
        className
      )}
      {...props}
    />
  );
});

// Content
const sideStyles = {
  right:
    "inset-y-0 right-0 h-full w-[90vw] max-w-md border-l data-[state=open]:slide-in-from-right data-[state=closed]:slide-out-to-right",
  left: "inset-y-0 left-0 h-full w-[90vw] max-w-md border-r data-[state=open]:slide-in-from-left data-[state=closed]:slide-out-to-left",
  top: "inset-x-0 top-0 w-full h-[50vh] border-b data-[state=open]:slide-in-from-top data-[state=closed]:slide-out-to-top",
  bottom:
    "inset-x-0 bottom-0 w-full h-[50vh] border-t data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom",
};

const DrawerContent = React.forwardRef(function DrawerContent(
  { className, children, showClose = true, ...props },
  ref
) {
  const { side, setPortalContainer } = useDrawer();

  const setRefs = React.useCallback(
    (node) => {
      setPortalContainer(node);
      if (typeof ref === "function") {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
    },
    [ref, setPortalContainer]
  );

  return (
    <Dialog.Portal>
      <DrawerOverlay />
      <Dialog.Content
        ref={setRefs}
        className={cn(
          "fixed z-panel bg-card text-card-foreground border-border shadow-xl flex flex-col",
          "transition ease-in-out duration-300",
          "data-[state=open]:animate-in data-[state=closed]:animate-out",
          sideStyles[side],
          className
        )}
        {...props}>
        <VisuallyHidden>
          <Dialog.Title>Dialog Title</Dialog.Title>
        </VisuallyHidden>
        <VisuallyHidden>
          <Dialog.Description>Dialog Description</Dialog.Description>
        </VisuallyHidden>
        {showClose && (
          <Dialog.Close className='absolute right-4 top-4 rounded-md p-2 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors'>
            ✕
          </Dialog.Close>
        )}
        {children}
      </Dialog.Content>
    </Dialog.Portal>
  );
});

// Header
function DrawerHeader({ className, ...props }) {
  return (
    <div
      className={cn(
        "p-4 border-b border-border font-semibold text-lg text-foreground",
        className
      )}
      {...props}
    />
  );
}

// Body
function DrawerBody({ className, ...props }) {
  return (
    <div className={cn("p-4 overflow-y-auto flex-1", className)} {...props} />
  );
}

// Footer
function DrawerFooter({ className, ...props }) {
  return (
    <div
      className={cn(
        "p-4 border-t border-border flex justify-end gap-2 bg-card",
        className
      )}
      {...props}
    />
  );
}

// Close
const DrawerClose = Dialog.Close;

// Attach compound components
Drawer.Trigger = DrawerTrigger;
Drawer.Content = DrawerContent;
Drawer.Header = DrawerHeader;
Drawer.Body = DrawerBody;
Drawer.Footer = DrawerFooter;
Drawer.Close = DrawerClose;
